// Phase I3 — Intelligence Verdict (3-column).
// Confidence | Value Edge · Sharp | Market Consensus.
// Faithful port of prototype/super-acca.jsx::VerdictPanel (lower half).

import { ConfMeter, Eyebrow } from "../../../components/super-acca";
import { TIERS } from "../../../super-acca/categories";
import type { SuperAccaSlip } from "../../../super-acca/view-models";

export interface IntelligenceVerdictProps {
  slip: SuperAccaSlip;
}

export function IntelligenceVerdict({ slip }: IntelligenceVerdictProps) {
  const tier = TIERS[slip.tier];
  const foundation = slip.legs.filter((l) => l.layer === "FOUNDATION");
  const edgeLeg = foundation[0] ?? slip.legs[0] ?? null;
  const sharp = slip.sharp_money;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 1,
        background: "var(--sa-hairline)",
        border: "1px solid var(--sa-hairline)",
        borderRadius: "0 0 8px 8px",
        overflow: "hidden",
      }}
    >
      {/* Confidence */}
      <div style={{ background: "var(--sa-bg-panel)", padding: 24 }}>
        <Eyebrow>Confidence</Eyebrow>
        <div
          style={{
            fontFamily: "var(--sa-font-stat)",
            fontSize: 80,
            lineHeight: 0.9,
            color:
              (slip.confidence ?? 0) >= 70
                ? "var(--sa-signal-green)"
                : "var(--sa-accent-saffron)",
            marginTop: 14,
          }}
        >
          {slip.confidence ?? "—"}
          <span style={{ fontSize: 28, color: "var(--sa-fg-muted)" }}>/100</span>
        </div>
        <div style={{ marginTop: 12 }}>
          <ConfMeter
            value={slip.confidence ?? 0}
            label={`${tier.label} FLOOR · ${tier.min_conf}`}
          />
        </div>
      </div>
      {/* Value Edge */}
      <div style={{ background: "var(--sa-bg-panel)", padding: 24 }}>
        <Eyebrow>Value Edge · Sharp</Eyebrow>
        <div
          style={{
            fontFamily: "var(--sa-font-stat)",
            fontSize: 80,
            lineHeight: 0.9,
            color: "var(--sa-accent-saffron)",
            marginTop: 14,
          }}
        >
          {edgeLeg?.edge_pct !== null && edgeLeg?.edge_pct !== undefined
            ? `+${edgeLeg.edge_pct}`
            : "—"}
          <span style={{ fontSize: 28, color: "var(--sa-amber-dim)" }}>PP</span>
        </div>
        {edgeLeg ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              marginTop: 14,
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
            }}
          >
            <div>
              <div
                style={{
                  color: "var(--sa-fg-muted)",
                  letterSpacing: "0.14em",
                  fontSize: 9.5,
                }}
              >
                MODEL
              </div>
              <div style={{ color: "var(--sa-fg-primary)", marginTop: 3 }}>
                {edgeLeg.model_prob ?? "—"}%
              </div>
            </div>
            <div>
              <div
                style={{
                  color: "var(--sa-fg-muted)",
                  letterSpacing: "0.14em",
                  fontSize: 9.5,
                }}
              >
                MARKET
              </div>
              <div style={{ color: "var(--sa-fg-primary)", marginTop: 3 }}>
                {edgeLeg.market_prob ?? "—"}%
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {/* Market Consensus */}
      <div style={{ background: "var(--sa-bg-panel)", padding: 24 }}>
        <Eyebrow>Market Consensus</Eyebrow>
        <div
          style={{
            fontFamily: "var(--sa-font-stat)",
            fontSize: 80,
            lineHeight: 0.9,
            color:
              sharp.signal === "CONFIRMS"
                ? "var(--sa-signal-green)"
                : "var(--sa-signal-warn)",
            marginTop: 14,
          }}
        >
          {sharp.signal ?? "—"}
        </div>
        <div
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            color: "var(--sa-fg-muted)",
            marginTop: 14,
            letterSpacing: "0.14em",
          }}
        >
          {sharp.open !== null && sharp.now !== null
            ? `● SHARP MONEY · LINE ${sharp.open.toFixed(2)} → ${sharp.now.toFixed(2)}`
            : "● SHARP MONEY · awaiting line move"}
        </div>
      </div>
    </div>
  );
}
